import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import BookerDetailsComponentPage from '../../components/Reports/Reports/BookerDetailsReport/BookerDetailsComponentPage';
import GuestDetailsComponentPage from '../../components/Reports/Reports/GuestDetailsReport/GuestDetailsComponentPage';
import { HostPaymentsReportPage } from '../../components/Reports/Reports/HostPaymentsReport/HostPaymentReportPage';
import ReportComponentPage from '../../components/Reports/Reports/ReservationsReportPage';
import TopBookersComponentPage from '../../components/Reports/Reports/TopBookersReport/TopBookersComponentPage';
import TopGuestsComponentPage from '../../components/Reports/Reports/TopGuestsReport/TopGuestsComponentPage';
import TopModulesComponentPage from '../../components/Reports/Reports/TopModulesReport/TopModulesComponentPage';
import {
    canAccessReport,
    getReadableReportDescription,
    getReadableReportName,
} from '../../components/Reports/ReportsMapping';
import GuestInvoicesReportPage from './GuestInvoicesReportPage';
import MailingListsReportPage from '../../components/Reports/Reports/MailingListReport/MailingListReportPage';

export enum ReportType {
    Monthly = 'monthlyReport',
    Quarterly = 'quarterlyReport',
    YTD = 'ytdReport',
    FY = 'fyReport',
    Reservations = 'reservationsReport',
    Cancellations = 'cancellationReport',
    Declined = 'declinedReport',
}

export default function IndividualReportPage() {
    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const urlRef = window.location.href.split('/')[4];
    const navigate = useNavigate();
    const theme = useTheme();

    const onClickGoBack = () => {
        if (urlRef === 'guests') {
            navigate(`/reports/guestDetails`);
        } else {
            navigate(`/reports`);
        }
    };

    useEffect(() => {
        if (!canAccessReport(urlRef, loggedInUser.role)) {
            navigate(`/permissionDenied`, {
                state: {
                    name: 'reports',
                },
            });
        }
    }, [loggedInUser.role, urlRef]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Grid
            sx={{
                width: '100%',
                height: isMobile ? '100%' : 'auto',
                justifyContent: 'left',
                alignItems: 'center',
            }}
        >
            <Grid
                sx={{
                    width: '100%',
                    height: isMobile || isIpad ? '2%' : '5%',
                    justifyContent: 'left',
                    display: 'flex',
                }}
            >
                <Typography
                    variant='h1'
                    sx={{
                        textAlign: 'left',
                        textTransform: 'uppercase',
                    }}
                >
                    {getReadableReportName('type', urlRef)}
                </Typography>
            </Grid>

            <Grid
                sx={{
                    width: '100%',
                    minHeight: isMobile || isIpad ? '3%' : '5%',
                    justifyContent: 'left',
                    paddingTop: '10px',
                }}
            >
                <Typography
                    sx={{
                        fontSize: '.75rem',
                    }}
                >
                    {' '}
                    {getReadableReportDescription('type', urlRef)}.
                    {/* {urlRef === 'cancellationReport'
                            ? ' Cancelled reservations are based on the reservation start date '
                            : null} */}
                </Typography>
                <Typography
                    sx={{
                        fontSize: '.75rem',
                        paddingBottom: isMobile || isIpad ? '30px' : '0px',
                    }}
                >
                    {urlRef === 'cancellationReport'
                        ? 'NOTE: The following results are based on cancelled reservations within the date range selected. The date range is based on the start date of the reservation, not the cancelled date.'
                        : null}
                </Typography>
            </Grid>

            <Grid
                sx={{
                    width: '100%',
                    minHeight: 'auto',
                    justifyContent: 'left',
                    paddingTop: '20px',
                }}
            >
                {(() => {
                    switch (urlRef) {
                        case 'topGuests':
                            return <TopGuestsComponentPage />;
                        case 'topExperiences':
                            return <TopModulesComponentPage />;
                        case 'topBookers':
                            return <TopBookersComponentPage />;
                        case 'guestDetails':
                            return <GuestDetailsComponentPage />;
                        case 'bookerDetails':
                            return <BookerDetailsComponentPage />;
                        case 'cancellationReport':
                            return (
                                <ReportComponentPage
                                    reportType={ReportType.Cancellations}
                                />
                            );
                        case 'declinedReport':
                            return (
                                <ReportComponentPage
                                    reportType={ReportType.Declined}
                                />
                            );
                        case 'monthlyReport':
                            return (
                                <ReportComponentPage
                                    reportType={ReportType.Monthly}
                                />
                            );
                        case 'quarterlyReport':
                            return (
                                <ReportComponentPage
                                    reportType={ReportType.Quarterly}
                                />
                            );
                        case 'ytdReport':
                            return (
                                <ReportComponentPage
                                    reportType={ReportType.YTD}
                                />
                            );
                        case 'fyReport':
                            return (
                                <ReportComponentPage
                                    reportType={ReportType.FY}
                                />
                            );
                        case 'reservationsReport':
                            return (
                                <ReportComponentPage
                                    reportType={ReportType.Reservations}
                                />
                            );
                        case 'guests':
                            return <GuestDetailsComponentPage />;
                        case 'hostPayments':
                            return <HostPaymentsReportPage />;
                        case 'guestInvoices':
                            return <GuestInvoicesReportPage />;
                        case 'mailingLists':
                            return <MailingListsReportPage />;
                        default:
                            return null;
                    }
                })()}
            </Grid>
            <Grid
                sx={{
                    width: '50%',
                    display:
                        loggedInUser?.role.includes('owner') ||
                        isMobile ||
                        isIpad
                            ? 'none'
                            : 'flex', // Hide on mobile
                    justifyContent: 'flex-start',
                }}
            >
                {' '}
                <CustomButton
                    onClick={onClickGoBack}
                    color='primary'
                    sx={{
                        marginLeft: '-8px',
                        marginBottom: '10px',
                        '&:hover': {
                            cursor: 'pointer',
                            color: theme.palette.textAccent.main,
                            backgroundColor: 'transparent',
                            background: 'none',
                        },
                    }}
                >
                    <ArrowBackIcon /> {/* {isMobile ? null : ( */}
                    <Typography
                        sx={{
                            fontSize: '.75rem',
                            paddingLeft: '5px',
                        }}
                    >
                        Back to Reports Menu
                    </Typography>
                    {/* )} */}
                </CustomButton>
            </Grid>
        </Grid>
    );
}
